import { Box, Typography, Divider, Chip } from '@mui/material';
import { grey } from '@mui/material/colors';

function Progression({ progression, capitalizeWords }) {
    if (!progression) {
        return null;
    }
    
    // Handle new API structure where progression is directly the work experiences object
    // or old structure where it's nested in progression.workExperience
    const workExperiencesData = progression && typeof progression === 'object' && !Array.isArray(progression) && !progression.workExperience
      ? progression  // New format: progression is directly the work experiences object
      : (progression?.workExperience || null);  // Old format or array format
    
    // Check if progression is an object (new format) or array (old format)
    const isNewFormat = workExperiencesData && typeof workExperiencesData === 'object' && !Array.isArray(workExperiencesData);

    if (!workExperiencesData || (isNewFormat && Object.keys(workExperiencesData).length === 0)) {
        return null;
    }

    if (isNewFormat) {
        // New format: object with companies as keys
        const companies = Object.entries(workExperiencesData).slice(0, 3); // Get first 3 companies
        
        return (
            <Box sx={{ mx: 1 }}>
                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, width: '100%' }}>
                        {companies.map(([company, positions], index) => (
                            <Box sx={{ px: 1, py: 1, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: grey[100], width: '100%' }} key={index}>
                                <Typography variant="body1" sx={{ color: '#444', fontWeight: 600 }}>
                                    {capitalizeWords(company)}
                                </Typography>
                                
                                {/* Show all positions within this company */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                                    {positions.map((position, posIndex) => (
                                        <Box key={posIndex} sx={{ pl: 1, borderLeft: '3px solid #1BE885', backgroundColor: '#fff', borderRadius: 1 }}>
                                            <Typography variant="subtitle1" sx={{ color: '#666' }}>
                                                {capitalizeWords(position.title)}
                                            </Typography>
                                            <Typography variant="subtitle1" sx={{ color: '#888' }}>
                                                <strong>Tenure:</strong> {position.tenure}
                                            </Typography>
                                            {position.location && (
                                                <Typography variant="body2" sx={{ color: '#888' }}>
                                                    <strong>Location:</strong> {position.location}
                                                </Typography>
                                            )}
                                        </Box>
                                    ))}
                                </Box> 
                            </Box>
                        ))}
                    </Box>
                    <Divider sx={{ mb: 2, mt: 2 }} />
                </Box>
            </Box>
        );
    } else {
        // Old format: array of experiences
        // Group experiences by company
        const companyGroups = {};
        workExperiencesData.slice(0, 9).forEach(experience => { // Get more experiences to group by company
            if (!companyGroups[experience.company]) {
                companyGroups[experience.company] = [];
            }
            companyGroups[experience.company].push(experience);
        });
        
        // Get the last 3 companies
        const companies = Object.entries(companyGroups).slice(0, 3);
        
        return (
            <Box sx={{ mx: 2 }}>
                {companies.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {companies.map(([company, experiences], index) => (
                                <Box sx={{ 
                                    p: 3, 
                                    border: '1px solid #e0e0e0', 
                                    borderRadius: 2,
                                    backgroundColor: '#fafafa'
                                }} key={index}>
                                    <Typography variant="h5" sx={{ color: '#444', fontWeight: 600, mb: 2 }}>
                                        {capitalizeWords(company)}
                                    </Typography>
                                    
                                    {/* Show all experiences within this company */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {experiences.map((experience, expIndex) => (
                                            <Box key={expIndex} sx={{ 
                                                pl: 2, 
                                                borderLeft: '3px solid #1BE885',
                                                backgroundColor: '#fff',
                                                p: 2,
                                                borderRadius: 1
                                            }}>
                                                <Typography variant="h6" sx={{ color: '#666', fontWeight: 500, mb: 1 }}>
                                                    {capitalizeWords(experience.title)}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: '#888' }}>
                                                    <strong>Tenure:</strong> {experience.tenure}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                        <Divider sx={{ mb: 2, mt: 2 }} />
                    </Box>
                )}
            </Box>
        );
    }
}

export default Progression;